import React from 'react';
import {createClientMessage} from 'react-chatbot-kit';
import {CommentRole} from '../../../@types/llm-conversation';
import {ResourceType} from '../../../@types/resource-type';
import LlmService from '../../../services/llm.service';
import config, {getArchiveKey} from './config.js';
import {toast} from 'react-toastify';

const convertToLlmContent = (state, newMessage, timestamp, includeESD) => {
    const { messages } = state;
    const contents = messages.map((message) => {
        return {
            role: message.type === 'user' ? CommentRole.USER : CommentRole.SYSTEM,
            content: message.message,
            timestamp: timestamp
        };
    });
    const newMessageBloated = includeESD ? newMessage + '. Use hyperlink style to highlihght the node name. ' : newMessage;
    return [...contents, {
        role: CommentRole.USER,
        content: newMessageBloated,
        timestamp: timestamp
    }];
};

const ActionProvider = ({ createChatBotMessage, setState, state, children }) => {
    const [includeESD, setIncludeESD] = React.useState(false);
    const [llmModel, setLlmModel] = React.useState('');
    const timestamp = new Date().toISOString();

    // Store current workspace object ref for localStorage operations
    const currentWorkspaceRef = React.useRef(null);    

    const handleReply = (message, currentWorkspaceObject) => {
        // Store workspace object for localStorage operations
        currentWorkspaceRef.current = currentWorkspaceObject;

        const contents = convertToLlmContent(state, message, timestamp, includeESD);
        const eventSequenceId = currentWorkspaceObject && currentWorkspaceObject.objectType === ResourceType.EVENT_SEQUENCE_TREES ? currentWorkspaceObject.object.id : null;
        const conversationHistory = {
            eventSequenceId: eventSequenceId || 0,
            includeESD: currentWorkspaceObject && currentWorkspaceObject.objectType === ResourceType.EVENT_SEQUENCE_TREES ? includeESD : false,
            comments: contents,
            llm: llmModel,
            queryTime: timestamp,
            error: {
                code: 0,
                message: '',
                details: ''
            }
        };

        const thinkingMessage = createChatBotMessage('Thinking ...', {
            widget: 'thinking',
        });
        setState((prev) => ({
            ...prev,
            messages: [...prev.messages, thinkingMessage],
        }));

        LlmService.postLlmConversation(conversationHistory).then((response) => {
            const llmAnswer = response.data.comments[response.data.comments.length - 1].content;
            const botMessage = createChatBotMessage(llmAnswer);
            if (response.data.error.code != 0) {
                botMessage.message = JSON.parse(response.data.error.message).error.message;
            }
            setState((prev) => {
                const updatedMessages = [...prev.messages.slice(0, -1), botMessage];
                const key = eventSequenceId ? getArchiveKey(eventSequenceId) : config.archiveName;
                localStorage.setItem(key, JSON.stringify(updatedMessages));
                return { ...prev, messages: updatedMessages };
            });
        }).catch((error) => {
            const botMessage = createChatBotMessage('Sorry, I could not process your request at this time. Please try again later.');
            setState((prev) => {
                const updatedMessages = [...prev.messages.slice(0, -1), botMessage];
                const key = eventSequenceId ? getArchiveKey(eventSequenceId) : config.archiveName;
                localStorage.setItem(key, JSON.stringify(updatedMessages));
                return { ...prev, messages: updatedMessages };
            });
            toast.error(error.message);
        });
    };  

    const handleUploadFile = ({ name }) => {
        const userMessage = createClientMessage(`${name} File uploaded`,
            {
                widget: 'uploadFile',
                payload: { file: name },
            },
        );
        setState((prev) => ({
            ...prev,
            messages: [...prev.messages, userMessage],
        }));
    };

    const handleIncludeESD = (checked) => {
        setIncludeESD(checked);
    };

    const handleLLMModel = (llm) => {
        setLlmModel(llm);
    }
    
    const handleTriggerReply = (message, currentWorkspaceObject) => {
        // Store workspace object for localStorage operations
        currentWorkspaceRef.current = currentWorkspaceObject;

        const matches = message.toLowerCase().match(/clear history\s?(-?[0-9]*)$/);
        if (matches) {
            let count = matches[1] ? parseInt(matches[1]) : 0;
            clearMessages(count);
            return;
        }


        const userMessage = createClientMessage(message);
        setState((prev) => ({
            ...prev,
            messages: [...prev.messages, userMessage],
        }));

        handleReply(message, currentWorkspaceObject);
    }

    const clearMessages = (count) => {
        setState((prev) => {
            const updatedMessages = [...prev.messages.slice(0, count)];
            const currentWS = currentWorkspaceRef.current;
            const esId = currentWS && currentWS.objectType === ResourceType.EVENT_SEQUENCE_TREES
                ? currentWS.object.id : null;
            const key = esId ? getArchiveKey(esId) : config.archiveName;
            localStorage.setItem(key, JSON.stringify(updatedMessages));
            return { ...prev, messages: updatedMessages };
        });
    };
    
    return (
        <div>
            {React.Children.map(children, (child) => {
                return React.cloneElement(child, {
                    actions: {
                        handleReply,
                        handleIncludeESD,
                        handleLLMModel,
                        handleUploadFile,
                        clearMessages,
                        handleTriggerReply,
                    },
                });
            })}
        </div>
    );
};

export default ActionProvider;